import { useNavigate } from "@/app/hooks/useNavigate";
import { MODULES } from "@/app/lib/constants";
import UserMenu from "@/features/header/components/UserMenu";
import { cva, VariantProps } from "class-variance-authority";
import { cn } from "@/app/common/common-functions/functions";
import {
  BookUser,
  Calculator,
  CircleEllipsis,
  CircleHelp,
  CirclePlus,
  Cog,
  File,
  House,
  LayoutDashboard,
  Lightbulb,
  Megaphone,
  Menu,
  Newspaper,
  Search,
  Shield,
  SquareKanban,
  Star,
  Store,
  X,
} from "lucide-react";
import { forwardRef, useState } from "react";
import NewMenu from "@/features/header/components/NewMenu";
import { useIntercom } from "react-use-intercom";
import FavoritesDialog from "@/features/header/components/FavoritesDialog";
import ToolsMenu from "@/features/header/components/ToolsMenu";
import SearchDialog from "@/features/header/components/SearchDialog";
import logo from "@/assets/images/entegral-base-white-fix.png";
import DealTrackerNotEnabledDialog from "@/features/header/components/DealTrackerNotEnabledDialog";
import NewDealDialog from "@/features/header/components/NewDealDialog";
import { CalculatorsDialog } from "@/features/tools/CalculatorsDialog";
import { useStore } from "@/app/stores/store";
import { toJS } from "mobx";

type HeaderProps = {
  displayName?: string;
  modules?: number[];
  userId?: string;
  logout?: () => void;
  isLoginAs?: boolean;
  onNewEventClick?: () => void;
  systemLogo?: string;
  isAdmin?: boolean;
  isReadOnly?: boolean;
  noticeBoardAccess?: boolean;
};

const navButtonVariants = cva(
  "tw-min-w-16 tw-w-fit tw-flex tw-h-full tw-cursor-pointer tw-flex-col tw-gap-1 tw-pt-1 tw-px-3 tw-items-center tw-justify-center",
  {
    variants: {
      variant: {
        primary: "tw-text-primary",
        default: "tw-text-color4",
        user: "tw-text-color7",
      },
      size: {
        mobile: "",
        desktop: "",
      },
      disabled: {
        true: "tw-opacity-80",
      },
    },
    compoundVariants: [
      {
        variant: "primary",
        size: "desktop",
        className: "tw-font-bold hover:tw-bg-color1tint",
      },
      {
        variant: "default",
        size: "desktop",
        className: "tw-font-semibold hover:tw-bg-color1tint",
      },
      {
        variant: "user",
        size: "desktop",
        className: "tw-font-semibold hover:tw-bg-color2tint",
      },
    ],
    defaultVariants: {
      variant: "primary",
      size: "desktop",
    },
  }
);

const NavButton = forwardRef<HTMLDivElement, { children: React.ReactNode; onClick?: () => void; className?: string } & VariantProps<typeof navButtonVariants>>(
  ({ children, onClick, variant, size, className, disabled, ...props }, ref) => {
    return (
      <div ref={ref} className={cn(navButtonVariants({ variant, size, disabled }), className)} onClick={onClick} {...props}>
        {children}
      </div>
    );
  }
);

const Header = ({ displayName, modules, userId, logout, isLoginAs, onNewEventClick, systemLogo, isAdmin, isReadOnly, noticeBoardAccess }: HeaderProps) => {
  const navigate = useNavigate();
  const [isFavoritesOpen, setIsFavoritesOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isDealTrackerNotEnabledDialogOpen, setIsDealTrackerNotEnabledDialogOpen] = useState(false);
  const [isDealSearchOpen, setIsDealSearchOpen] = useState(false);
  const [isCalculatorsOpen, setIsCalculatorsOpen] = useState(false);
  const intercom = useIntercom();
  const {
    userStore: { userSession },
  } = useStore();

  const session = toJS(userSession);

  const countryCode = session?.countryCode;
  const currencySymbol = session?.currencyDisplay;

  // TODO: Improve navigation once react router is updated
  const toolsMenuItems = [
    {
      label: "File Library",
      icon: <File className="tw-size-5" />,
      onClick: () => navigate("/library"),
    },
    {
      label: "News",
      icon: <Newspaper className="tw-size-5" />,
      onClick: () => navigate("/library", { state: { view: 2 } }),
    },
    noticeBoardAccess && {
      label: "Notice Board",
      icon: <Megaphone className="tw-size-5" />,
      onClick: () => navigate("/library", { state: { view: 3 } }),
    },
    session?.country == "South Africa" && {
      label: "Insights",
      icon: <Lightbulb className="tw-size-5" />,
      onClick: () => navigate("/property", { state: { view: 5, viewDate: new Date() } }),
    },
    {
      label: "Vault",
      icon: <Shield className="tw-size-5" />,
      onClick: () => window.open("https://vault.entegral.net", "_blank"),
    },
    {
      label: "Marketplace",
      icon: <Store className="tw-size-5" />,
      onClick: () => window.open("https://myproperty.market", "_blank"),
    },
    {
      label: "Calculators",
      icon: <Calculator className="tw-size-5" />,
      onClick: () => setIsCalculatorsOpen(true),
    },
  ].filter((e) => !!e);

  const hasDealTracker = !!modules?.includes(MODULES.DEAL_TRACKER);

  const handleDealTrackerClick = () => {
    if (hasDealTracker) {
      navigate("/deal");
    } else {
      setIsDealTrackerNotEnabledDialogOpen(true);
    }
  };

  const handleNewDealClick = () => {
    if (hasDealTracker) {
      setIsDealSearchOpen(true);
    } else {
      setIsDealTrackerNotEnabledDialogOpen(true);
    }
  };

  return (
    <>
      <FavoritesDialog isOpen={isFavoritesOpen} onClose={() => setIsFavoritesOpen(false)} />
      <SearchDialog isOpen={isSearchOpen} onClose={() => setIsSearchOpen(false)} />
      <DealTrackerNotEnabledDialog
        isOpen={isDealTrackerNotEnabledDialogOpen}
        onClose={() => setIsDealTrackerNotEnabledDialogOpen(false)}
        onRequestAccess={() => navigate("/settings/integrations")}
        isAdmin={isAdmin}
      />
      <NewDealDialog isOpen={isDealSearchOpen} onClose={() => setIsDealSearchOpen(false)} />
      <CalculatorsDialog open={isCalculatorsOpen} onOpenChange={setIsCalculatorsOpen} countryCode={countryCode} currencySymbol={currencySymbol} />

      <div className="tw-fixed tw-left-0 tw-right-0 tw-top-0 tw-z-50 tw-flex tw-h-[75px] tw-bg-foreground tw-text-xs">
        <div className="tw-bg-prim tw-h-full tw-min-w-[15px] tw-bg-primary"></div>
        <div className="tw-flex tw-h-full tw-cursor-pointer tw-items-center tw-px-[35px]" onClick={() => navigate("/")}>
          <img src={systemLogo || logo} alt="logo" className="tw-mt-[-8px] tw-max-h-[45px] tw-max-w-[140px] tw-cursor-pointer" />
        </div>
        <div className="tw-ml-auto tw-flex tw-items-center lg:tw-hidden">
          <NavButton variant="primary" onClick={() => setIsSearchOpen(true)}>
            <Search className="tw-size-5" />
          </NavButton>
          {!isReadOnly && (
            <NewMenu onNewEventClick={onNewEventClick} onNewDealClick={handleNewDealClick} modules={modules} isAdmin={isAdmin}>
              <NavButton variant="primary">
                <CirclePlus className="tw-size-5" />
              </NavButton>
            </NewMenu>
          )}
          <NavButton variant="default" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
            {isMobileMenuOpen ? <X className="tw-size-6" /> : <Menu className="tw-size-6" />}
          </NavButton>
        </div>

        {/* Desktop Navigation Menu */}
        <div className="tw-hidden tw-h-full tw-w-full tw-items-center tw-justify-end tw-px-2 lg:tw-flex">
          <NavButton variant="primary" onClick={() => setIsSearchOpen(true)}>
            <Search className="tw-size-5" />
            Find
          </NavButton>
          {!isReadOnly && (
            <NewMenu onNewEventClick={onNewEventClick} onNewDealClick={handleNewDealClick} modules={modules} isAdmin={isAdmin}>
              <NavButton variant="primary">
                <CirclePlus className="tw-size-5" />
                New
              </NavButton>
            </NewMenu>
          )}
          <NavButton variant="default" onClick={() => navigate("/")}>
            <LayoutDashboard className="tw-size-5" />
            Home
          </NavButton>
          <NavButton variant="default" onClick={() => navigate("/property")}>
            <House className="tw-size-5" />
            Listings
          </NavButton>
          <NavButton variant="default" onClick={() => navigate("/contacts")}>
            <BookUser className="tw-size-5" />
            Contacts
          </NavButton>
          <NavButton variant="default" onClick={handleDealTrackerClick} disabled={!hasDealTracker}>
            <SquareKanban className="tw-size-5" />
            Deals
          </NavButton>
          <ToolsMenu toolItems={toolsMenuItems}>
            <NavButton variant="default">
              <CircleEllipsis className="tw-size-5" />
              Tools
            </NavButton>
          </ToolsMenu>
        </div>
        <div className="tw-hidden tw-h-full tw-w-full tw-max-w-60 tw-items-center tw-bg-primary tw-px-2 lg:tw-flex">
          <UserMenu userId={userId} logout={logout} isLoginAs={isLoginAs}>
            <NavButton variant="user">
              <Cog className="tw-size-5" />
              {displayName}
            </NavButton>
          </UserMenu>
          <NavButton variant="user" onClick={() => setIsFavoritesOpen(true)}>
            <Star className="tw-size-5" />
            Favourites
          </NavButton>
          <NavButton variant="user" onClick={() => intercom.show()}>
            <CircleHelp className="tw-size-5" />
            Help
          </NavButton>
        </div>
      </div>

      {/* Mobile Navigation Menu */}
      <div
        className={cn(
          "tw-fixed tw-left-0 tw-right-0 tw-top-[75px] tw-z-40 lg:tw-hidden",
          isMobileMenuOpen ? "tw-visible tw-opacity-100" : "tw-pointer-events-none tw-invisible tw-opacity-0"
        )}
      >
        <div className="tw-flex tw-items-center tw-justify-center tw-bg-foreground tw-px-8 tw-py-6">
          <NavButton variant="default" onClick={() => navigate("/")} size="mobile">
            <LayoutDashboard className="tw-size-5" />
            Home
          </NavButton>
          <NavButton variant="default" onClick={() => navigate("/property")} size="mobile">
            <House className="tw-size-5" />
            Listings
          </NavButton>
          <NavButton variant="default" onClick={() => navigate("/contacts")} size="mobile">
            <BookUser className="tw-size-5" />
            Contacts
          </NavButton>
          <NavButton variant="default" onClick={handleDealTrackerClick} size="mobile" disabled={!hasDealTracker}>
            <SquareKanban className="tw-size-5" />
            Deals
          </NavButton>
        </div>

        <div className="tw-flex tw-flex-wrap tw-justify-center tw-gap-5 tw-bg-color1tint tw-px-8 tw-py-4 tw-text-color4">
          {toolsMenuItems.map((item) => (
            <button className="tw-flex tw-cursor-pointer tw-items-start tw-gap-x-2 tw-gap-y-3 tw-text-start tw-text-sm" onClick={item.onClick}>
              {item.icon}
              {item.label}
            </button>
          ))}
        </div>

        <div className="tw-flex tw-items-center tw-justify-center tw-bg-primary tw-px-8 tw-py-4">
          <UserMenu userId={userId} logout={logout} isLoginAs={isLoginAs}>
            <NavButton variant="user" size="mobile">
              <Cog className="tw-size-5" />
              {displayName}
            </NavButton>
          </UserMenu>
          <NavButton variant="user" onClick={() => setIsFavoritesOpen(true)} size="mobile">
            <Star className="tw-size-5" />
            Favourites
          </NavButton>
          <NavButton variant="user" onClick={() => intercom.show()} size="mobile">
            <CircleHelp className="tw-size-5" />
            Help
          </NavButton>
        </div>
      </div>
    </>
  );
};

export default Header;
